import { Plus, SearchLoop } from 'assets/icons';
import Button from 'components/button';
import StyledInput from 'components/styledInput/StyledInput';
import UseContext from 'hooks/useContext';
import { useEffect, useMemo, useState } from 'react';
import ApiDataBase from 'server';
import TemplateCard from './templateCard';
import { filterTemplates } from './missionTemplateFormat';

/** First screen of a mission: the company, then one of its job descriptions. */
const TemplateSelectStep = ({
  onOpenTemplate,
  onCreateTemplate,
  onTemplatesLoaded,
}) => {
  const { dataCreateMission, setDataCreateMission } = UseContext();
  const token = localStorage.getItem('xsrfToken');
  const [companyList, setCompanyList] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadFailed, setHasLoadFailed] = useState(false);
  const [search, setSearch] = useState('');
  const idCompany = dataCreateMission.company;

  useEffect(() => {
    if (!token) return;
    ApiDataBase.getCompaniesForMissionCreation(
      { token },
      (data) => {
        const list = (data?.companies || []).map((company) => ({
          value: company._id,
          label: `${company.name_commercial} - ${company.reason_social}`,
          name: company.name_commercial,
          location: company.location,
          checked: company._id === idCompany,
        }));
        setCompanyList(list);
        // A company chosen before this screen opened (from a group's page)
        // may carry neither its name nor its address yet.
        const selected = list.find((item) => item.value === idCompany);
        if (selected) {
          setDataCreateMission((prev) =>
            prev.companyName === selected.name && (prev.idLocation || !selected.location)
              ? prev
              : { ...prev, companyName: selected.name, idLocation: prev.idLocation || selected.location || null },
          );
        }
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, [token, idCompany]);

  useEffect(() => {
    if (!token || !idCompany) {
      setTemplates([]);
      onTemplatesLoaded?.([]);
      return;
    }
    setIsLoading(true);
    setHasLoadFailed(false);
    ApiDataBase.getAdminMissionTemplates(
      { token, idCompany },
      (data) => {
        const list = data?.templates || [];
        setTemplates(list);
        // The wizard checks the name typed against the names already taken here.
        onTemplatesLoaded?.(list);
        setIsLoading(false);
      },
      (err) => {
        if (err.name === 'CanceledError') return;
        console.error(err);
        setHasLoadFailed(true);
        setIsLoading(false);
      },
    );
    // onTemplatesLoaded is a setState: stable, so it stays out of the deps.
  }, [token, idCompany, dataCreateMission.missionTemplate]);

  const onCompanyChange = (updatedCompanyList) => {
    const checkedItem = updatedCompanyList.find((item) => item.checked);
    setSearch('');
    // Changing company drops the job description chosen for the previous one.
    setDataCreateMission((prev) => ({
      ...prev,
      company: checkedItem?.value || '',
      companyName: checkedItem?.name || '',
      idLocation: checkedItem?.location || null,
      missionTemplate: null,
    }));
  };

  const visibleTemplates = useMemo(
    () => filterTemplates(templates, search),
    [templates, search],
  );

  return (
    <div className="flex flex-col gap-6">
      <StyledInput
        type="selectMultipleRadio"
        label="Sélectionner l'entreprise"
        value={
          companyList.find((item) => item.value === idCompany)?.label ||
          'Sélectionner'
        }
        list={companyList.map((item) => ({
          ...item,
          checked: item.value === idCompany,
        }))}
        onChange={onCompanyChange}
        isSearchable
        dropdownFullWidth
      />

      {idCompany ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col">
            <span className="ft-xl font-medium text-gray-900">
              Fiche de poste
            </span>
            <span className="ft-sm text-gray-500">
              Choisissez la fiche de poste sur laquelle la mission sera créée.
            </span>
          </div>

          <div className="flex items-center gap-3">
            <StyledInput
              type="text"
              className="flex-1"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher.."
              imgBack={<SearchLoop wh={16} color="#6B7280" />}
            />
            <Button size="LGI" color="PRIMARY" onClick={onCreateTemplate}>
              <Plus wh={18} color="#FFFFFF" />
              Créer
            </Button>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-2 gap-3">
              {[1, 2, 3, 4].map((index) => (
                <div
                  key={index}
                  className="h-28 skeleton-bar rounded-lg"
                />
              ))}
            </div>
          ) : null}

          {!isLoading && hasLoadFailed ? (
            <span className="ft-sm text-red-500">
              Impossible de charger les fiches de poste. Veuillez réessayer.
            </span>
          ) : null}

          {!isLoading && !hasLoadFailed && visibleTemplates.length === 0 ? (
            <span className="ft-sm text-gray-500">
              {templates.length === 0
                ? "Cette entreprise n'a pas encore de fiche de poste. Créez-en une pour elle."
                : 'Aucune fiche ne correspond à cette recherche.'}
            </span>
          ) : null}

          {!isLoading && visibleTemplates.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {visibleTemplates.map((template) => (
                <TemplateCard
                  key={template._id}
                  template={template}
                  onOpen={onOpenTemplate}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <span className="ft-sm text-gray-500">
          Sélectionnez une entreprise pour voir ses fiches de poste.
        </span>
      )}
    </div>
  );
};

export default TemplateSelectStep;
